import React, { useState } from 'react';
import { GIButton, Modal } from 'app-components';
import { FormattedMessage } from 'react-intl/dist';
import styled from 'styled-components';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import { ModalContextType } from '../../../../contexts/modalContext';
import useDateValidation from '../../hooks/useDateValidation';
import useApiHandling from '../../hooks/useApiHandling';
import { ValidationErrorMessage, useAppSelector, calculateOverrideSEPEndWithFormat } from '../../utils';

const StyledField = styled.div`
    position: fixed;
    .date-picker,
    .shown.error--msg {
        margin-top: 4px !important;
        padding: 8px;
        width: 100%;
        background-color: ${({ theme }) => theme.dangerLightest};
    }
    .react-datepicker-wrapper {
        max-width: 195px;
    }
    @media (max-width: 768px) {
        flex-direction: column;
        justify-content: center;
        padding-right: 32px;
        .error--msg {
            max-width: 100%;
        }
    }

    .gi-input {
        text-align: left;
    }
`;

const StyledControls = styled.div`
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 100px;

    @media (max-width: 768px) {
        margin-top: 0;
        flex-direction: column;
        gap: 5px;
    }
`;

interface IModalProps extends ModalContextType {
    caseNumber: string;
    coverageYear: string;
}

const OverrideSepQepDenialModal = ({ closeModal, caseNumber }: IModalProps) => {
    const { gracePeriod } = useAppSelector(state => state.capApplications.appControllerInfo);
    const { date } = calculateOverrideSEPEndWithFormat(gracePeriod);
    const { submitReverseDenial } = useApiHandling();
    const [newSEPEndDate, setNewSEPEndDate] = useState<Date | null>(date);
    const { errorMsg, textReplacements, validateSepEndDate } = useDateValidation(gracePeriod);

    const handleDateChange = (date: Date | null) => {
        setNewSEPEndDate(date);
        date && validateSepEndDate(date);
    };

    function onClose() {
        closeModal('overrideSepQepDenialModal');
    }

    function onContinue() {
        submitReverseDenial(caseNumber, newSEPEndDate);
    }

    return (
        <Modal id="override_sep_qep_denial_modal">
            <Modal.Header
                id="override_sep_qep_denial_modal_title"
                titleId="cap.applications.modal.overridesepdenial"
                onHide={onClose}
            />
            <Modal.Body>
                <FormattedMessage id="cap.applications.modal.overridesepdenialdate" />

                <StyledField>
                    <DatePicker
                        labelText=""
                        wrapperClasses={['date-picker']}
                        isRequired
                        id="dob"
                        type="text"
                        pattern="MM/DD/YYYY"
                        value={newSEPEndDate}
                        withMask={true}
                        onDateChange={value => handleDateChange(value || null)}
                    />

                    <ValidationErrorMessage
                        isVisible={errorMsg !== null}
                        messageText={errorMsg}
                        textReplacements={textReplacements ? textReplacements : ''}
                    />
                </StyledField>
            </Modal.Body>
            <Modal.Controls>
                <StyledControls>
                    <GIButton
                        id="button"
                        onClickHandler={onContinue}
                        isDisabled={errorMsg !== null}
                        variant="primary"
                        labelId="button.continue"
                    />
                    <GIButton id="button" onClickHandler={onClose} variant="secondary" labelId="button.cancel" />
                </StyledControls>
            </Modal.Controls>
        </Modal>
    );
};

export default OverrideSepQepDenialModal;
